import { useState, useEffect } from 'react'
import {
  MdArrowBack,
  MdBusiness,
  MdFlight,
  MdLocationOn,
  MdPhone,
  MdEmail,
  MdLanguage,
  MdSchedule,
  MdSecurity,
  MdTrendingUp,
  MdEdit,
  MdDelete,
  MdRefresh,
  MdFacebook,
  MdAlternateEmail,
  MdCameraAlt,
  MdWork,
  MdWarning,
  MdCheckCircle,
  MdPauseCircle,
  MdCancel,
} from 'react-icons/md'
import { useDispatch } from 'react-redux'
import { useParams, useNavigate } from 'react-router-dom'
import { Button, LoadingSpinner, InfoCard, Badge, StatCard } from '../../components/ui'
import { useAirline } from '../../hooks/useAirline'
import { useWebsocket } from '../../hooks/useWebsocket'
import { addSubscription, removeSubscription } from '../../state/websocket.slice'
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  calculateAirlineAge,
  extractDomain,
} from '../../utils/airline'
import { getWebsocketChannel } from '../../utils/websocket'

function AirlineDetailPage() {
  const dispatch = useDispatch()
  const { airlineId } = useParams()
  const navigate = useNavigate()
  const { subscribeToChannel, unsubscribeFromChannel } = useWebsocket()
  const [airline, setAirline] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  const { getAirlineAsync, isGetAirlineLoading } = useAirline()

  useEffect(() => {
    if (airlineId) {
      loadAirline()
    }
  }, [airlineId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const channelName = getWebsocketChannel(airlineId).airlineDetails

    if (airlineId && channelName) {
      subscribeToChannel(channelName)
      dispatch(addSubscription(channelName))
    }

    return () => {
      if (airlineId && channelName) {
        unsubscribeFromChannel(channelName)
        dispatch(removeSubscription(channelName))
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const loadAirline = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const airlineData = await getAirlineAsync(airlineId)
      setAirline(airlineData)
    } catch (error) {
      console.error('Error loading airline:', error)
      setError('Failed to load airline details')
    } finally {
      setIsLoading(false)
    }
  }

  const handleBack = () => {
    navigate(-1)
  }

  const handleEdit = () => {
    navigate(`/airlines/${airlineId}/edit`)
  }

  const handleDelete = () => {
    // TODO: Implement delete confirmation modal
    console.log('Delete airline:', airlineId)
  }

  const handleRefresh = () => {
    loadAirline()
  }

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return <MdCheckCircle className="text-success-600 dark:text-success-400" />
      case 'inactive':
        return <MdPauseCircle className="text-neutral-600 dark:text-neutral-400" />
      case 'suspended':
        return <MdWarning className="text-warning-600 dark:text-warning-400" />
      case 'bankrupt':
        return <MdCancel className="text-error-600 dark:text-error-400" />
      default:
        return <MdBusiness className="text-neutral-600 dark:text-neutral-400" />
    }
  }

  const getSocialMediaIcon = (platform) => {
    switch (platform) {
      case 'facebook':
        return <MdFacebook />
      case 'twitter':
        return <MdAlternateEmail />
      case 'instagram':
        return <MdCameraAlt />
      case 'linkedin':
        return <MdWork />
      default:
        return <MdLanguage />
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !airline) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="text-center py-12">
          <MdBusiness className="mx-auto h-12 w-12 text-neutral-400" />
          <h3 className="mt-2 text-sm font-medium text-neutral-900 dark:text-white">
            {error || 'Airline not found'}
          </h3>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            The airline you're looking for doesn't exist or has been removed.
          </p>
          <div className="mt-6">
            <Button variant="primary" onClick={handleBack}>
              Back to Airlines
            </Button>
          </div>
        </div>
      </div>
    )
  }

  const airlineAge = calculateAirlineAge(airline.founded)

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-start mb-4">
          <Button variant="ghost" onClick={handleBack} className="flex items-center space-x-2">
            <MdArrowBack />
            <span>Back</span>
          </Button>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center space-x-4">
            <div className="flex-1 min-w-0 text-left">
              <h1 className="text-2xl font-bold text-neutral-900 dark:text-white mb-2">
                {airline.name}
              </h1>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  {getStatusIcon(airline.status)}
                  <Badge
                    variant={
                      airline.status === 'active'
                        ? 'success'
                        : airline.status === 'suspended'
                          ? 'warning'
                          : 'default'
                    }
                  >
                    {airline.status}
                  </Badge>
                </div>
                <span className="text-neutral-600 dark:text-neutral-400">
                  {airline.iataCode} / {airline.icaoCode}
                </span>
                {airline.callsign && (
                  <span className="text-neutral-600 dark:text-neutral-400">
                    Callsign: {airline.callsign}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              onClick={handleRefresh}
              disabled={isGetAirlineLoading}
              className="flex items-center space-x-2"
            >
              <MdRefresh className={isGetAirlineLoading ? 'animate-spin' : ''} />
              <span>Refresh</span>
            </Button>

            <Button
              variant="secondary"
              onClick={handleEdit}
              className="flex items-center space-x-2"
            >
              <MdEdit />
              <span>Edit</span>
            </Button>

            <Button variant="danger" onClick={handleDelete} className="flex items-center space-x-2">
              <MdDelete />
              <span>Delete</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Airline Logo and Basic Info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2">
          <InfoCard title="Airline Information" icon={MdBusiness} className="h-full">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                    Country
                  </label>
                  <p className="text-neutral-900 dark:text-white">{airline.country}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                    Founded
                  </label>
                  <p className="text-neutral-900 dark:text-white">
                    {formatDate(airline.founded)} {airlineAge && `(${airlineAge} years)`}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                    Alliance
                  </label>
                  <p className="text-neutral-900 dark:text-white">{airline.alliance || 'None'}</p>
                </div>

                {airline.frequentFlyerProgram && (
                  <div>
                    <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                      Frequent Flyer Program
                    </label>
                    <p className="text-neutral-900 dark:text-white">
                      {airline.frequentFlyerProgram.name}
                    </p>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                    Fleet Size
                  </label>
                  <p className="text-neutral-900 dark:text-white">
                    {airline.fleetSize || 0} aircraft
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                    Destinations
                  </label>
                  <p className="text-neutral-900 dark:text-white">
                    {airline.destinations?.length || 0} cities
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                    Hub Airports
                  </label>
                  <p className="text-neutral-900 dark:text-white">
                    {airline.hubAirports?.length || 0} hubs
                  </p>
                </div>

                {airline.website && (
                  <div>
                    <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                      Website
                    </label>
                    <a
                      href={airline.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      {extractDomain(airline.website)}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </InfoCard>
        </div>

        <div className="lg:col-span-1">
          <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
            <div className="w-32 h-32 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center mx-auto mb-4">
              {airline.logooo ? (
                <img
                  src={airline.logo}
                  alt={`${airline.name} logo`}
                  className="w-24 h-24 rounded object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none'
                    e.target.nextSibling.style.display = 'flex'
                  }}
                />
              ) : null}
              <MdBusiness
                className="text-white text-4xl"
                style={{ display: airline.logoo ? 'none' : 'flex' }}
              />
            </div>

            <div className="text-center">
              <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">
                {airline.name}
              </h3>
              <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
                {airline.iataCode} / {airline.icaoCode}
              </p>

              <div className="flex items-center justify-center space-x-2 mb-4">
                {getStatusIcon(airline.status)}
                <Badge
                  variant={
                    airline.status === 'active'
                      ? 'success'
                      : airline.status === 'suspended'
                        ? 'warning'
                        : 'default'
                  }
                >
                  {airline.status}
                </Badge>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Fleet Size"
          value={airline.fleetSize || 0}
          subtitle="Aircraft in service"
          icon={MdFlight}
          iconBgColor="bg-primary-100 dark:bg-primary-900"
          iconColor="text-primary-600 dark:text-primary-400"
        />

        <StatCard
          title="Destinations"
          value={airline.destinations?.length || 0}
          subtitle="Cities served"
          icon={MdLocationOn}
          iconBgColor="bg-success-100 dark:bg-success-900"
          iconColor="text-success-600 dark:text-success-400"
        />

        <StatCard
          title="Hub Airports"
          value={airline.hubAirports?.length || 0}
          subtitle="Main bases"
          icon={MdSchedule}
          iconBgColor="bg-info-100 dark:bg-info-900"
          iconColor="text-info-600 dark:text-info-400"
        />

        {airline.financialInfo?.annualRevenue && (
          <StatCard
            title="Annual Revenue"
            value={formatCurrency(
              airline.financialInfo.annualRevenue,
              airline.financialInfo.currency,
            )}
            subtitle={`Fiscal Year ${airline.financialInfo.fiscalYear}`}
            icon={MdTrendingUp}
            iconBgColor="bg-warning-100 dark:bg-warning-900"
            iconColor="text-warning-600 dark:text-warning-400"
          />
        )}
      </div>

      {/* Detailed Information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Headquarters */}
        {airline.headquarters && (
          <InfoCard
            title="Headquarters"
            icon={MdLocationOn}
            iconBgColor="bg-success-100 dark:bg-success-900"
            iconColor="text-success-600 dark:text-success-400"
          >
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  City
                </label>
                <p className="text-neutral-900 dark:text-white">{airline.headquarters.city}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  Country
                </label>
                <p className="text-neutral-900 dark:text-white">{airline.headquarters.country}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  Address
                </label>
                <p className="text-neutral-900 dark:text-white whitespace-pre-line">
                  {airline.headquarters.address}
                </p>
              </div>
            </div>
          </InfoCard>
        )}

        {/* Contact Information */}
        {airline.contactInfo && (
          <InfoCard
            title="Contact Information"
            icon={MdPhone}
            iconBgColor="bg-info-100 dark:bg-info-900"
            iconColor="text-info-600 dark:text-info-400"
          >
            <div className="space-y-4">
              {airline.contactInfo.customerService && (
                <div>
                  <h4 className="font-medium text-neutral-900 dark:text-white mb-2">
                    Customer Service
                  </h4>
                  <div className="space-y-1">
                    {airline.contactInfo.customerService.phone && (
                      <div className="flex items-center space-x-2 text-sm">
                        <MdPhone className="text-neutral-500" />
                        <span className="text-neutral-600 dark:text-neutral-400">
                          {airline.contactInfo.customerService.phone}
                        </span>
                      </div>
                    )}
                    {airline.contactInfo.customerService.email && (
                      <div className="flex items-center space-x-2 text-sm">
                        <MdEmail className="text-neutral-500" />
                        <span className="text-neutral-600 dark:text-neutral-400">
                          {airline.contactInfo.customerService.email}
                        </span>
                      </div>
                    )}
                    {airline.contactInfo.customerService.hours && (
                      <div className="flex items-center space-x-2 text-sm">
                        <MdSchedule className="text-neutral-500" />
                        <span className="text-neutral-600 dark:text-neutral-400">
                          {airline.contactInfo.customerService.hours}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {airline.contactInfo.reservations && (
                <div>
                  <h4 className="font-medium text-neutral-900 dark:text-white mb-2">
                    Reservations
                  </h4>
                  <div className="space-y-1">
                    {airline.contactInfo.reservations.phone && (
                      <div className="flex items-center space-x-2 text-sm">
                        <MdPhone className="text-neutral-500" />
                        <span className="text-neutral-600 dark:text-neutral-400">
                          {airline.contactInfo.reservations.phone}
                        </span>
                      </div>
                    )}
                    {airline.contactInfo.reservations.email && (
                      <div className="flex items-center space-x-2 text-sm">
                        <MdEmail className="text-neutral-500" />
                        <span className="text-neutral-600 dark:text-neutral-400">
                          {airline.contactInfo.reservations.email}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </InfoCard>
        )}
      </div>

      {/* Additional Information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Destinations */}
        {airline.destinations && airline.destinations.length > 0 && (
          <InfoCard
            title="Destinations"
            icon={MdLocationOn}
            iconBgColor="bg-primary-100 dark:bg-primary-900"
            iconColor="text-primary-600 dark:text-primary-400"
          >
            <div className="flex flex-wrap gap-2">
              {airline.destinations.map((destination, index) => (
                <Badge key={index} variant="default" size="sm">
                  {destination}
                </Badge>
              ))}
            </div>
          </InfoCard>
        )}

        {/* Hub Airports */}
        {airline.hubAirports && airline.hubAirports.length > 0 && (
          <InfoCard
            title="Hub Airports"
            icon={MdFlight}
            iconBgColor="bg-success-100 dark:bg-success-900"
            iconColor="text-success-600 dark:text-success-400"
          >
            <div className="flex flex-wrap gap-2">
              {airline.hubAirports.map((hub, index) => (
                <Badge key={index} variant="success" size="sm">
                  {hub}
                </Badge>
              ))}
            </div>
          </InfoCard>
        )}
      </div>

      {/* Regulatory and Financial Information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Regulatory Information */}
        {airline.regulatoryInfo && (
          <InfoCard
            title="Regulatory Information"
            icon={MdSecurity}
            iconBgColor="bg-warning-100 dark:bg-warning-900"
            iconColor="text-warning-600 dark:text-warning-400"
          >
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  Operating Certificate
                </label>
                <p className="text-neutral-900 dark:text-white font-mono text-sm">
                  {airline.regulatoryInfo.operatingCertificate}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  Certificate Expiry
                </label>
                <p className="text-neutral-900 dark:text-white">
                  {formatDateTime(airline.regulatoryInfo.certificateExpiry)}
                </p>
              </div>

              {airline.regulatoryInfo.safetyRating && (
                <div>
                  <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                    Safety Rating
                  </label>
                  <Badge
                    variant={
                      airline.regulatoryInfo.safetyRating === 'Excellent'
                        ? 'success'
                        : airline.regulatoryInfo.safetyRating === 'Good'
                          ? 'primary'
                          : airline.regulatoryInfo.safetyRating === 'Satisfactory'
                            ? 'warning'
                            : 'error'
                    }
                  >
                    {airline.regulatoryInfo.safetyRating}
                  </Badge>
                </div>
              )}
            </div>
          </InfoCard>
        )}

        {/* Social Media */}
        {airline.socialMedia && Object.keys(airline.socialMedia).length > 0 && (
          <InfoCard
            title="Social Media"
            icon={MdLanguage}
            iconBgColor="bg-info-100 dark:bg-info-900"
            iconColor="text-info-600 dark:text-info-400"
          >
            <div className="space-y-2">
              {Object.entries(airline.socialMedia).map(([platform, url]) => (
                <a
                  key={platform}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 text-sm text-neutral-600 dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  {getSocialMediaIcon(platform)}
                  <span className="capitalize">{platform}</span>
                </a>
              ))}
            </div>
          </InfoCard>
        )}
      </div>
    </div>
  )
}

export default AirlineDetailPage
